import noop from 'lodash/noop';
import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import PauseSmallIcon from '../../../../static/icons/ic_pause_small_24.svg';
import PlaySmallIcon from '../../../../static/icons/ic_play_small_24.svg';
import AddStationIcon from '../../../../static/icons/ic_stations_add.svg';
import StationRingsIcon from '../../../../static/icons/station_rings.svg';
import Img from '../../../components/ImageLoader/ImageLoader';
import Eq from '../../../components/lib/Eq/Eq';
import { messages as accessMessages } from '../../../constants/AccessibilityConstants';
import { KeyCodes } from '../../../constants/App';
import { AudioPlayerActions } from '../../../constants/AudioPlayer';
import { BadgeTypes } from '../../../constants/Badge';
import QATags from '../../../constants/QATags';
import { SourceCardConstants } from '../../../constants/SourceCard';
import { getDefaultArt } from '../../../lib/ArtUtils';
import { AppContextIntegrationData } from '../../../lib/Context';
import { handlerNoPropagate } from '../../../lib/EventUtils';
import * as RouteUtils from '../../../lib/RouteUtils';
import { hexStringToRgba } from '../../../lib/StringUtils';
import { createBEM } from '../../../lib/utils/css';
import Icon from '../../Icon/Icon';
import Badge from '../Badge/Badge';
import Button from '../simple/Button/Button';
import Link from '../simple/Link/Link';
import SourceCard from '../SourceCard/SourceCard';

import { GridItemText } from './constants';

import './GridItem.scss';

export class GridItem extends React.PureComponent {
  static propTypes = {
    canOverlap: PropTypes.bool,
    canShuffle: PropTypes.bool,
    defaultPlaceholderArt: PropTypes.string,
    doesFade: PropTypes.bool,
    dominantColor: PropTypes.string,
    ellipsisText: PropTypes.node,
    enlargeOnHover: PropTypes.bool,
    hasAddIcon: PropTypes.bool,
    hasTakeoverModes: PropTypes.bool,
    hoverText: PropTypes.node,
    id: PropTypes.string,
    imgUrl: PropTypes.string,
    itemOnclick: PropTypes.func,
    itemOnclickUrl: PropTypes.string,
    onFocus: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onSourceCardToggle: PropTypes.func,
    pandoraType: PropTypes.string,
    pauseButtonOnclick: PropTypes.func,
    playButtonOnclick: PropTypes.func,
    renderMetaInfoOverThumbnail: PropTypes.bool,
    shuffleOnclick: PropTypes.func,
    shuffleOnclickIfDisabled: PropTypes.func,
    sourceCardMenuItems: PropTypes.array,
    twoColumns: PropTypes.bool,
    undoHover: PropTypes.bool,
    useDefaultCursor: PropTypes.bool,

    isBadgeShortened: PropTypes.bool,
    isCircle: PropTypes.bool,
    isClean: PropTypes.bool,
    isCollected: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isExplicit: PropTypes.bool,
    isFluid: PropTypes.bool,
    isHosted: PropTypes.bool,
    isLarge: PropTypes.bool,
    isOnlyArt: PropTypes.bool,
    isPaused: PropTypes.bool,
    isPlaceholder: PropTypes.bool,
    isPlaying: PropTypes.bool,
    isRadioOnly: PropTypes.bool,
    isShuffleDisabled: PropTypes.bool,
    isStation: PropTypes.bool,
    isUnavailable: PropTypes.bool,

    placeholderBgColor: PropTypes.string,
    placeholderHideMetaInfo: PropTypes.bool,
    placeholderIcon: PropTypes.func,
    placeholderOnClick: PropTypes.func,
    placeholderText: PropTypes.string,

    mainText: PropTypes.node,
    mainTextModifier: PropTypes.node,
    mainTextOnclick: PropTypes.func,
    mainTextUrl: PropTypes.any,

    secondText: PropTypes.node,
    secondTextModifier: PropTypes.string,
    secondTextOnclick: PropTypes.func,
    secondTextUrl: PropTypes.string,

    thirdText: PropTypes.node,
    thirdTextModifier: PropTypes.string,
    thirdTextOnclick: PropTypes.func,
    thirdTextUrl: PropTypes.string,

    qaButton: PropTypes.oneOf(values(QATags)),
    qaImg: PropTypes.oneOf(values(QATags)),
    qaMainText: PropTypes.oneOf(values(QATags)),
    qaSecondText: PropTypes.oneOf(values(QATags)),
    qaThirdText: PropTypes.oneOf(values(QATags)),
  };

  state = {
    isHovered: false,
    isSourceCardOpen: false,
    isStation: this.props.isStation || RouteUtils.isStationBasePath(this.props.mainTextUrl),
    isTriggerFocusedOrHovered: false,
  };

  componentDidUpdate = () => {
    if (this.props.undoHover && (this.state.isHovered || this.state.isSourceCardOpen)) {
      this.sourceCardToggleCallback(false);
      this.onMouseLeave();
    }
  };

  static eventData = {
    pause: { componentEventType: AudioPlayerActions.PAUSE },
    play: { componentEventType: AudioPlayerActions.AUDIO_PLAY_TRACK },
  };

  isInteractive = () => {
    return (this.state.isHovered || this.state.isSourceCardOpen) && !this.props.hasAddIcon;
  };

  itemOnclick = e => {
    (this.props.itemOnclick || noop)(e);
  };

  placeholderOnClick = () => {
    if (this.props.placeholderOnClick) {
      this.props.placeholderOnClick();
    }
  };

  renderButton = (isVisible, playOnclick, pauseOnclick, isPlaying, dataQa) => {
    if (playOnclick && pauseOnclick) {
      const eventData = isPlaying ? GridItem.eventData.pause : GridItem.eventData.play;
      const onClick = isPlaying ? pauseOnclick : playOnclick;
      const defaultQaTag = isPlaying ? QATags.HMPB : QATags.HMPSB;
      const hiddenMessage = isPlaying
        ? accessMessages.pauseSourceName
        : accessMessages.playSourceName;
      const classes = createBEM('GridItem__button', {
        disabled: this.props.isDisabled,
        visible: isVisible,
      });
      return (
        <AppContextIntegrationData data={eventData}>
          <Button
            className={classes}
            onClick={onClick}
            aria-disabled={this.props.isDisabled}
            data-qa={dataQa || defaultQaTag}
          >
            {isPlaying ? (
              <Icon className='iconPause' glyph={PauseSmallIcon} />
            ) : (
              <Icon className='iconPlay' glyph={PlaySmallIcon} />
            )}
            <span className='visuallyHidden'>
              <FormattedMessage {...hiddenMessage} values={{ sourceName: this.props.mainText }} />
            </span>
          </Button>
        </AppContextIntegrationData>
      );
    }
  };

  renderCollectedBadge = isCollected => {
    if (isCollected) {
      const classes = createBEM('GridItem__badge', {
        collected: true,
      });
      return (
        <div className={classes}>
          <Badge badgeType={BadgeTypes.COLLECTED} isIcon={true} />
        </div>
      );
    }
  };

  renderEllipsis = (sourceCard, isVisible) => {
    if (sourceCard) {
      const gridItemEllipsisClasses = createBEM('GridItem__ellipsis', {
        hidden: !isVisible,
      });
      const gridItemEllipsisTextClasses = createBEM('GridItem__ellipsis__text', {
        hidden: !this.state.isTriggerFocusedOrHovered,
      });
      return (
        <div
          className={gridItemEllipsisClasses}
          onMouseEnter={() => {
            this.sourceCardTriggerToggleFocusOrHover(true);
          }}
          onMouseLeave={() => {
            this.sourceCardTriggerToggleFocusOrHover(false);
          }}
        >
          <div className={gridItemEllipsisTextClasses}>
            {this.props.ellipsisText || <FormattedMessage {...GridItemText.moreText} />}
          </div>
          <div>{!this.props.undoHover && sourceCard}</div>
        </div>
      );
    }
  };

  renderEQ = (isVisible, isPlaying) => {
    if (isVisible) {
      return <Eq isPlaying={isPlaying} />;
    }
  };

  getIntegrationData = () => {
    return {
      id: this.props.id,
    };
  };

  renderImg = () => {
    if (this.props.hasAddIcon) {
      return (
        <div className='GridItem__thumbnail__icon'>
          <Icon glyph={AddStationIcon} />
        </div>
      );
    }
    const { imgUrl, dominantColor, qaImg, doesFade } = this.props;
    if (imgUrl) {
      const bgColor = dominantColor && `#${dominantColor}`;
      const bgClass = dominantColor && 'GridItem__imgBg';
      return (
        <AppContextIntegrationData data={this.getIntegrationData}>
          <Img
            imgClass='GridItem__img'
            key={this.props.id + ' image'}
            src={imgUrl}
            doesFade={doesFade}
            data-qa={qaImg}
            bgColor={bgColor}
            bgClass={bgClass}
          />
        </AppContextIntegrationData>
      );
    }
  };

  renderTakeoverModeBadge = () => {
    return <Badge isBlue={true} isInline={true} badgeType={BadgeTypes.TAKEOVER_MODES} />;
  };

  renderMainText = () => {
    const { mainText, mainTextUrl, mainTextOnclick, qaMainText, mainTextModifier } = this.props;
    const className = createBEM('GridItem__caption__main', {
      disabled: this.props.isUnavailable,
      hosted: this.props.isHosted,
    });
    return this.getTextNode(
      className,
      mainText,
      mainTextUrl,
      mainTextOnclick,
      qaMainText,
      mainTextModifier,
    );
  };

  renderSecondText = () => {
    const {
      secondText,
      secondTextUrl,
      secondTextOnclick,
      qaSecondText,
      secondTextModifier,
      thirdText,
    } = this.props;
    const subTextClass = 'GridItem__caption__second';

    let badges;
    if (this.props.hasTakeoverModes) {
      badges = this.renderTakeoverModeBadge();
    } else if (!thirdText) {
      badges = this.renderBadge();
    }

    return this.getTextNode(
      subTextClass,
      secondText,
      secondTextUrl,
      secondTextOnclick,
      qaSecondText,
      secondTextModifier,
      badges,
    );
  };

  renderThirdText = () => {
    const { thirdText, thirdTextUrl, thirdTextOnclick, qaThirdText, thirdTextModifier } =
      this.props;
    const subTextClass = 'GridItem__caption__third';
    return this.getTextNode(
      subTextClass,
      thirdText,
      thirdTextUrl,
      thirdTextOnclick,
      qaThirdText,
      thirdTextModifier,
      this.renderBadge(),
    );
  };

  getTextNode = (className, text, href, onClick, qaTag, contentModifer, badge) => {
    const handler = onClick ? onClick : noop;
    const content =
      href || onClick ? (
        <Link
          to={href || '#'}
          onClick={handler}
          data-qa={qaTag}
          aria-disabled={this.props.isUnavailable}
          className='GridItem__caption__link'
        >
          <span className='a11y--visuallyHidden'>{contentModifer}</span>
          {text}
        </Link>
      ) : (
        <span className='GridItem__caption__text'>{text}</span>
      );

    if (text || contentModifer) {
      return (
        <div className={className}>
          {contentModifer} {content} {badge}
        </div>
      );
    }
  };

  renderBadge = () => {
    const isAvailable = !(this.props.isUnavailable || this.props.isRadioOnly);
    const isExplicit = this.props.isExplicit && isAvailable;
    const isClean = this.props.isClean && isAvailable;
    const isBadgeShortened = this.props.isBadgeShortened;
    const badgeType = isExplicit
      ? BadgeTypes.EXPLICIT_SHORT
      : isBadgeShortened
      ? BadgeTypes.CLEAN_SHORT
      : BadgeTypes.CLEAN;

    if (!isExplicit && !isClean) {
      return;
    }

    return <Badge badgeType={badgeType} isInline={true} />;
  };

  renderShuffleBadge = () => {
    if (this.props.canShuffle) {
      const props = {
        badgeType: BadgeTypes.SHUFFLE,
        isIcon: true,
        isShuffleDisabled: this.props.isShuffleDisabled,
        onClick: this.props.shuffleOnclick,
        onClickIfShuffleDisabled: this.props.shuffleOnclickIfDisabled,
      };
      return <Badge {...props} />;
    }
  };

  renderRadioUnavailBadge = (isRadioOnly, isUnavailable, isVisible) => {
    if (isRadioOnly || isUnavailable) {
      const badgeType = isRadioOnly ? BadgeTypes.RADIO : BadgeTypes.UNAVAILABLE;
      const className = 'GridItem__badge';
      const badge = (
        <div className={className}>
          <Badge badgeType={badgeType} isOpaque={true} />
        </div>
      );
      return isVisible ? this.renderOverlayShield(badge, 'badge') : badge;
    }
  };

  getShieldIntegrationData = () => {
    return { destinationUrl: this.props.itemOnclickUrl };
  };

  renderDominantColorShield = (isVisible, shieldColor, hoverText, onClick, onClickUrl) => {
    const styles = {
      background:
        'linear-gradient(0deg, rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.1)), ' +
        hexStringToRgba(shieldColor || '000000', 0.3),
    };
    const classes = createBEM('GridItem__shield', {
      hidden: !isVisible,
      link: onClickUrl && !onClick,
    });

    const text = hoverText ? <div className='GridItem__shield__text'>{hoverText}</div> : null;
    if (onClick) {
      return (
        <AppContextIntegrationData data={this.getShieldIntegrationData}>
          <Button
            className={classes}
            style={styles}
            onClick={onClick}
            type='button'
            tabIndex={text ? '0' : '-1'}
          >
            {text}
          </Button>
        </AppContextIntegrationData>
      );
    }
    if (onClickUrl) {
      return (
        <AppContextIntegrationData data={this.getShieldIntegrationData}>
          <Link
            className={classes}
            style={styles}
            to={onClickUrl}
            tabIndex={this.props.renderMetaInfoOverThumbnail ? '0' : '-1'}
          >
            {text}
          </Link>
        </AppContextIntegrationData>
      );
    }
    return (
      <div className={classes} style={styles}>
        {text}
      </div>
    );
  };

  onMouseEnter = () => {
    if (this.props.onMouseEnter) {
      this.props.onMouseEnter();
    }
    this.setState({ isHovered: true });
  };

  onMouseLeave = () => {
    if (this.props.onMouseLeave) {
      this.props.onMouseLeave();
    }
    this.setState({ isHovered: false });
  };

  onFocus = () => {
    if (this.props.onFocus) {
      this.props.onFocus();
    }
    this.setState({ isHovered: true });
  };

  onBlur = () => {
    this.setState({ isHovered: false });
  };

  onKeyDown = e => {
    const key = e.which || e.keyCode;
    if (key === KeyCodes.TAB) {
      this.setState({ isHovered: false });
    }
  };

  sourceCardToggleCallback = isSourceCardOpen => {
    if (this.props.onSourceCardToggle) {
      this.props.onSourceCardToggle(isSourceCardOpen);
    }
    this.setState({ isSourceCardOpen });
  };

  sourceCardTriggerToggleFocusOrHover = isTriggerFocusedOrHovered => {
    this.setState({ isTriggerFocusedOrHovered });
  };

  renderOverlayShield = (child, type) => {
    const classes = createBEM('GridItem__shield', {
      [type]: !!type,
    });
    return <div className={classes}>{child}</div>;
  };

  renderThumbnailContent = () => {
    const showShield = this.isInteractive() || this.props.isPlaying || this.props.isPaused;
    const thumbClasses = createBEM('GridItem__thumbnail__content', {
      onlyArt: this.props.isOnlyArt,
    });
    return (
      <div className={thumbClasses}>
        {this.renderImg()}
        {this.state.isStation &&
          this.renderOverlayShield(<Icon glyph={StationRingsIcon} />, 'rings')}
        {this.renderDominantColorShield(
          showShield,
          this.props.dominantColor,
          this.props.hoverText,
          this.props.itemOnclick && handlerNoPropagate(this.itemOnclick),
          this.props.itemOnclickUrl,
        )}
        {this.renderRadioUnavailBadge(
          this.props.isRadioOnly,
          this.props.isUnavailable,
          !showShield,
        )}
        {this.renderEQ(
          !this.isInteractive() && (this.props.isPlaying || this.props.isPaused),
          this.props.isPlaying,
        )}
        {this.renderButton(
          this.isInteractive(),
          this.props.playButtonOnclick,
          this.props.pauseButtonOnclick,
          this.props.isPlaying,
          this.props.qaButton,
        )}
      </div>
    );
  };

  renderPlaceholder = () => {
    const classes = createBEM('GridItem', {
      placeholder: this.props.isPlaceholder,
      circle: this.props.isCircle,
      overlap: this.props.canOverlap,
      hover: this.isInteractive() && this.props.enlargeOnHover,
    });

    const renderPlaceholderImg = () => {
      if (this.props.canOverlap) {
        return this.props.placeholderBgColor ? (
          <Img
            bgColor={this.props.placeholderBgColor}
            bgClass='GridItem__imgBg'
            key={this.props.id + ' bgColor'}
          />
        ) : (
          <Img
            src={this.props.defaultPlaceholderArt || getDefaultArt()}
            bgClass='GridItem__imgBg'
            key={this.props.id + ' defaultArt'}
          />
        );
      }
      return (
        <Img
          useDefaultBgColor={true}
          bgClass='GridItem__imgBg'
          key={this.props.id + ' defaultBgColor'}
        />
      );
    };

    const includePlaceholderIconAndText = this.props.placeholderIcon || this.props.placeholderText;

    return (
      <div className={classes} onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
        <div className='GridItem__thumbnail'>
          <div className='GridItem__thumbnail__content'>
            {renderPlaceholderImg()}
            {includePlaceholderIconAndText && (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions
              <div className='GridItem__placeholder__content' onClick={this.placeholderOnClick}>
                {this.props.placeholderIcon && (
                  <div className='GridItem__placeholder__icon'>
                    <Icon className='iconPlaceholder' glyph={this.props.placeholderIcon} />
                  </div>
                )}
                {this.props.placeholderText && (
                  <div className='GridItem__placeholder__text'>{this.props.placeholderText}</div>
                )}
              </div>
            )}
          </div>
        </div>
        {!this.props.placeholderHideMetaInfo && (
          <div className='GridItem__metaInfo'>
            <div className='GridItem__caption'>
              <div className='GridItem__caption__main' />
              <div className='GridItem__caption__second' />
              <div className='GridItem__caption__third' />
            </div>
          </div>
        )}
      </div>
    );
  };

  renderSourceCard = () => {
    const sourceCardEllipsis = (
      <SourceCard
        isEllipsis={true}
        isEllipsisXsmall={true}
        isDark={true}
        sourceCardMenuItems={this.props.sourceCardMenuItems}
        onToggleActive={this.sourceCardToggleCallback}
        onTriggerToggleFocus={this.sourceCardTriggerToggleFocusOrHover}
        triggerHidden={!this.isInteractive()}
      />
    );

    return (
      <SourceCard
        type={[SourceCardConstants.CONTEXTMENU]}
        sourceCardMenuItems={this.props.sourceCardMenuItems}
        onToggleActive={this.sourceCardToggleCallback}
      >
        {this.renderThumbnailContent()}
        {this.props.renderMetaInfoOverThumbnail && this.renderMetaInfo()}
        {this.renderEllipsis(sourceCardEllipsis, this.isInteractive())}
      </SourceCard>
    );
  };

  renderMetaInfo = () => {
    const classes = createBEM('GridItem__metaInfo', {
      overThumbnail: this.props.renderMetaInfoOverThumbnail,
      hidden: !this.isInteractive() && this.props.renderMetaInfoOverThumbnail,
    });
    return (
      <div className={classes}>
        <div className='GridItem__caption'>
          {this.renderMainText()}
          {this.renderSecondText()}
          {!this.props.renderMetaInfoOverThumbnail && this.renderThirdText()}
        </div>
        {this.renderShuffleBadge()}
      </div>
    );
  };

  renderSourceCardOrThumbnailContent = () => {
    if (this.props.sourceCardMenuItems) {
      return this.renderSourceCard();
    }

    return (
      <React.Fragment>
        {this.renderThumbnailContent()}
        {this.props.renderMetaInfoOverThumbnail && this.renderMetaInfo()}
      </React.Fragment>
    );
  };

  render() {
    if (this.props.isPlaceholder) {
      return this.renderPlaceholder();
    }

    const classes = createBEM('GridItem', {
      canShuffle: this.props.canShuffle,
      circle: this.props.isCircle,
      fluid: this.props.isFluid,
      hover: this.isInteractive() && this.props.enlargeOnHover,
      large: this.props.isLarge,
      overlap: this.props.canOverlap,
      twoColumns: this.props.twoColumns,
    });
    const thumbnailClasses = createBEM('GridItem__thumbnail', {
      defaultCursor: this.props.useDefaultCursor,
    });

    return (
      <div className={classes}>
        <div
          className={thumbnailClasses}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
          onFocus={this.onFocus}
          onKeyDown={this.onKeyDown}
          onBlur={this.onBlur}
          data-qa={QATags.STI}
        >
          {this.renderSourceCardOrThumbnailContent()}
          {this.props.canOverlap
            ? this.renderCollectedBadge(this.props.isCollected && this.isInteractive())
            : this.renderCollectedBadge(this.props.isCollected)}
        </div>
        {!this.props.renderMetaInfoOverThumbnail && this.renderMetaInfo()}
      </div>
    );
  }
}

export default GridItem;
